import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Chip,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Popover,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import SendIcon from '@mui/icons-material/Send';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import DebitNoteDetails from '../components/debit-note/DebitNoteDetails';
import CustomerDetails from '../components/debit-note/CustomerDetails';
import DebitNoteItemsTable from '../components/debit-note/DebitNoteItemsTable';
import DebitNotePaymentSection from '../components/debit-note/DebitNotePaymentSection';
import DebitNoteSummary from '../components/debit-note/DebitNoteSummary';
import NotesSection from '../components/credit-note/NotesSection';
import { api } from '../utils/apiService';
import { salesService } from '../services';
import SelectInvoiceDialog from '../components/credit-note/SelectInvoiceDialog';

// Sample data
const customers = [
  { id: 1, name: 'Abdullah Trading Co.', vatNumber: '300123456700003', email: 'info@abdullahtrading.com' },
  { id: 2, name: 'Saudi Technical Industries', vatNumber: '300123456700004', email: 'info@sauditech.com' },
  { id: 3, name: 'Al-Riyadh Enterprises', vatNumber: '300123456700005', email: 'info@alriyadh.com' },
];

const products = [
  { id: 1, name: 'Product A', description: 'Description for Product A', price: 1000, vat: 15 },
  { id: 2, name: 'Product B', description: 'Description for Product B', price: 1500, vat: 15 },
  { id: 3, name: 'Product C', description: 'Description for Product C', price: 2000, vat: 15 },
];

// Add these style constants at the top of the component, after the imports
const controlStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    height: 36,
  },
  '& .MuiInputBase-input': {
    py: 0.75,
    fontSize: '0.875rem'
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem'
  },
  '& .MuiSelect-select': {
    py: 0.75
  }
};

const multilineStyle = {
  ...controlStyle,
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    minHeight: 36,
  }
};

// Fetch the next debit note number from the API
const fetchNextDebitNoteNumber = async () => {
  try {
    // Add a timestamp to prevent caching
    const timestamp = new Date().getTime();
    const response = await api.get(`/debitNotes/next-invoice-number?t=${timestamp}`);
    
    if (response.data.success) {
      const nextDebitNoteNumber = response.data.nextInvoiceNumber;
      return nextDebitNoteNumber;
    }
    console.warn('API did not return success, using fallback debit note number');
    return 'DN-00001'; // Fallback
  } catch (error) {
    console.error('Error fetching next debit note number:', error);
    return 'DN-00001'; // Fallback
  }
};

function NewDebitNote() {
  const navigate = useNavigate();
  const [debitNote, setDebitNote] = useState({
    debitNoteNumber: 'DN-00001', // Will be updated with API response
    date: new Date(),
    supplier: null,
    originalInvoice: {
      invoiceNo: '',
      invoiceDate: null,
      totalAmount: 0
    },
    reason: '',
    items: [],
    notes: '',
    status: 'Draft',
    subtotal: 0,
    vatAmount: 0,
    totalAmount: 0,
    paymentStatus: 'pending',
    paidAmt: 0,
    balanceAmt: 0,
    payments: [],
  });
  
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentReference, setPaymentReference] = useState('');

  const [newItem, setNewItem] = useState({
    product: null,
    description: '',
    quantity: 1,
    price: 0,
    vatRate: 15,
    discount: 0,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    description: false,
    discountPerc: false,
    discountAmt: false,
    netValue: false,
    priceWithVat: true,
    vatAmount: true,
    grandTotal: true
  });

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectInvoiceDialogOpen, setSelectInvoiceDialogOpen] = useState(false);

  // Fetch next debit note number when component loads
  useEffect(() => {
    const getNextDebitNoteNumber = async () => {
      try {
        setIsLoading(true);
        const nextNumber = await fetchNextDebitNoteNumber();
        setDebitNote(prevState => ({
          ...prevState,
          debitNoteNumber: nextNumber
        }));
      } catch (error) {
        console.error('Error setting next debit note number:', error);
      } finally {
        setIsLoading(false);
      }
    };

    getNextDebitNoteNumber();
  }, []);

  // Reset the debit note form after successful submission
  const resetDebitNoteForm = async (tenantId) => {
    const nextDebitNoteNumber = await fetchNextDebitNoteNumber();
    
    setDebitNote({
      debitNoteNumber: nextDebitNoteNumber,
      date: new Date(),
      supplier: null,
      originalInvoice: '',
      reason: '',
      items: [],
      notes: '',
      status: 'Draft',
      subtotal: 0,
      vatAmount: 0,
      totalAmount: 0,
      paymentStatus: 'pending',
      paidAmt: 0,
      balanceAmt: 0,
      payments: [],
      tenantId: tenantId
    });
    
    setNewItem({
      product: null,
      description: '',
      quantity: 1,
      price: 0,
      vatRate: 15,
      discount: 0,
    });
  };

  const handleSupplierChange = (event, newValue) => {
    setDebitNote({
      ...debitNote,
      supplier: newValue,
    });
  };

  const handleDateChange = (newDate) => {
    setDebitNote({
      ...debitNote,
      date: newDate,
    });
  };

  // Open the invoice selection dialog
  const openSelectInvoiceDialog = () => {
    setSelectInvoiceDialogOpen(true);
  };

  // Close the invoice selection dialog
  const closeSelectInvoiceDialog = () => {
    setSelectInvoiceDialogOpen(false);
  };

  // Handle invoice selection and populate debit note with invoice data
  const handleInvoiceSelect = (invoice) => {
    try {
      // Special case: if the invoice is 'open', open the select invoice dialog
      if (invoice === 'open') {
        openSelectInvoiceDialog();
        return;
      }
      
      setIsLoading(true);
      
      if (!invoice) {
        console.log('NewDebitNote: No invoice data received');
        setSelectedInvoice(null);
        return;
      }
      
      console.log('NewDebitNote: Received invoice data:', JSON.stringify(invoice, null, 2));
      
      // Check if the data is in the API response format with master and details
      const isMasterDetailsFormat = invoice.data && invoice.data.master && invoice.data.details;
      
      let invoiceData, detailsData;
      
      if (isMasterDetailsFormat) {
        console.log('NewDebitNote: Processing API response format with master and details');
        invoiceData = invoice.data.master;
        detailsData = invoice.data.details;
      } else {
        console.log('NewDebitNote: Processing standard invoice format');
        invoiceData = invoice;
        detailsData = invoice.items || [];
      }
      
      // Log the full structure of invoiceData to identify all available fields
      console.log('NewDebitNote: Full invoice data structure:', invoiceData);
      
      console.log('NewDebitNote: Invoice properties:', {
        invoiceNo: invoiceData.invoiceNo,
        invoiceDate: invoiceData.invoiceDate,
        customerName: invoiceData.customerName,
        customerAddress: invoiceData.customerAddress,
        vatNumber: invoiceData.vat,
        items: detailsData ? detailsData.length : 0
      });
      
      // First update the selectedInvoice state
      setSelectedInvoice(invoiceData);
      console.log('NewDebitNote: Selected invoice set:', invoiceData.invoiceNo);
      
      // Then directly update the debitNote state with the originalInvoice information
      // This ensures the DebitNoteDetails component gets the correct data immediately
      setDebitNote(prevState => ({
        ...prevState,
        originalInvoice: {
          invoiceNo: invoiceData.invoiceNo,
          invoiceDate: invoiceData.invoiceDate,
          totalAmount: invoiceData.totalAmt || 0
        }
      }));
      
      console.log('NewDebitNote: Updated debitNote with originalInvoice:', {
        invoiceNo: invoiceData.invoiceNo,
        invoiceDate: invoiceData.invoiceDate,
        totalAmount: invoiceData.totalAmt || 0
      });
      
      // Format items if they exist
      let formattedItems = [];
      if (detailsData && detailsData.length > 0) {
        console.log('NewDebitNote: Found', detailsData.length, 'items to format');
        console.log('NewDebitNote: First item sample:', detailsData[0]);
        console.log('NewDebitNote: Item fields available:', Object.keys(detailsData[0]));
        
        formattedItems = detailsData.map(item => {
          const formattedItem = {
            id: Date.now() + Math.random(),
            product: {
              id: item.itemCode || item.id,
              name: item.description || item.name,
              price: item.unitPrice || item.price,
              vat: item.vat || item.vatRate || 15
            },
            description: item.description || item.name,
            quantity: item.qty || item.quantity || 1,
            price: item.unitPrice || item.price,
            vatRate: item.vat || item.vatRate || 15,
            discount: item.discountPerc || item.discount || 0
          };
          console.log('NewDebitNote: Formatted item:', formattedItem);
          return formattedItem;
        });
      } else {
        console.log('NewDebitNote: No items found in invoice data');
      }
      
      // Update debit note with invoice data
      setDebitNote(prevState => {
        console.log('NewDebitNote: Previous debit note state:', prevState);
        console.log('NewDebitNote: DebitNote structure:', Object.keys(prevState));
        
        // Check if customer object exists in the current state
        if (prevState.customer) {
          console.log('NewDebitNote: Current customer fields:', Object.keys(prevState.customer));
        }
        
        // Log all available fields in invoiceData for debugging
        console.log('NewDebitNote: Available invoice fields for mapping:', Object.keys(invoiceData));
        
        // Create a properly structured debit note object with consistent field names
        const updatedDebitNote = {
          ...prevState,
          // Make sure originalInvoice structure matches what DebitNoteDetails component expects
          originalInvoice: {
            invoiceNo: invoiceData.invoiceNo,
            invoiceDate: invoiceData.invoiceDate,
            totalAmount: invoiceData.totalAmt || 0
          },
          // Make sure customer structure matches what CustomerDetails component expects
          customer: {
            name: invoiceData.customerName,
            code: invoiceData.customerCode,
            vatNumber: invoiceData.vat ? invoiceData.vat.toString() : '',
            streetName: invoiceData.customerStreetName || '',
            address: invoiceData.customerAddress || '',
            phone: invoiceData.customerPhone || '',
            postalCode: invoiceData.customerPostalCode || ''
          },
          // Add the formatted items
          items: formattedItems,
          // Add payment information if available
          paymentStatus: invoiceData.paymentStatus || 'pending',
          paidAmount: invoiceData.paidAmt || 0,
          balanceAmount: invoiceData.balanceAmt || invoiceData.totalAmt || 0
        };
        
        console.log('NewDebitNote: Original invoice data set:', updatedDebitNote.originalInvoice);
        console.log('NewDebitNote: Customer data set:', updatedDebitNote.customer);
        console.log('NewDebitNote: FULL updated debit note:', updatedDebitNote);
        
        // Calculate totals if we have items
        if (formattedItems.length > 0) {
          updatedDebitNote.subtotal = calculateSubtotal(formattedItems);
          updatedDebitNote.vatAmount = calculateVat(formattedItems);
          updatedDebitNote.totalAmount = calculateTotal(formattedItems);
          console.log('NewDebitNote: Calculated totals:', {
            subtotal: updatedDebitNote.subtotal,
            vatAmount: updatedDebitNote.vatAmount,
            totalAmount: updatedDebitNote.totalAmount
          });
        } else if (invoiceData.subTotal && invoiceData.vatAmt && invoiceData.totalAmt) {
          // Use the values from the invoice if no items are available
          updatedDebitNote.subtotal = invoiceData.subTotal;
          updatedDebitNote.vatAmount = invoiceData.vatAmt;
          updatedDebitNote.totalAmount = invoiceData.totalAmt;
          console.log('NewDebitNote: Using invoice totals:', {
            subtotal: updatedDebitNote.subtotal,
            vatAmount: updatedDebitNote.vatAmount,
            totalAmount: updatedDebitNote.totalAmount
          });
        }
        
        console.log('NewDebitNote: Final updated debit note:', JSON.stringify(updatedDebitNote, null, 2));
        return updatedDebitNote;
      });
    } catch (error) {
      console.error('Error handling invoice selection:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // The populateDebitNoteFromInvoice functionality has been integrated directly into handleInvoiceSelect

  const handleAddItem = () => {
    if (!newItem.product) return;
    
    const newItemId = Date.now(); // Use timestamp for unique ID
    
    setDebitNote({
      ...debitNote,
      items: [
        ...debitNote.items, 
        {
          id: newItemId,
          product: newItem.product,
          description: newItem.description || newItem.product.description,
          quantity: newItem.quantity,
          price: newItem.price || newItem.product.price,
          vatRate: newItem.vatRate || newItem.product.vat,
          discount: newItem.discount,
        }
      ],
    });
    
    setNewItem({
      product: null,
      description: '',
      quantity: 1,
      price: 0,
      vatRate: 15,
      discount: 0,
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = [...debitNote.items];
    newItems.splice(index, 1);
    setDebitNote({
      ...debitNote,
      items: newItems
    });
  };

  const calculateItemTotals = (item) => {
    const netValue = item.quantity * item.price;
    const discountAmount = (netValue * item.discount) / 100;
    const netValueAfterDiscount = netValue - discountAmount;
    const vatAmount = (netValueAfterDiscount * item.vatRate) / 100;
    const total = netValueAfterDiscount + vatAmount;

    return {
      netValue,
      discountAmount,
      netValueAfterDiscount,
      vatAmount,
      total
    };
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...debitNote.items];
    const updatedItem = {
      ...newItems[index],
      [field]: value,
      // Reset any calculated fields to ensure they're recalculated
      netValue: undefined,
      discountAmount: undefined,
      netValueAfterDiscount: undefined,
      vatAmount: undefined,
      total: undefined
    };
    
    const totals = calculateItemTotals(updatedItem);
    newItems[index] = {
      ...updatedItem,
      ...totals
    };
    
    setDebitNote({
      ...debitNote,
      items: newItems
    });
  };

  const handleProductChange = (event, newValue) => {
    setNewItem({
      ...newItem,
      product: newValue,
      price: newValue ? newValue.price : 0,
      description: newValue ? newValue.description : '',
    });
  };

  const calculateSubtotal = (items) => {
    return items.reduce((sum, item) => {
      const totals = calculateItemTotals(item);
      return sum + totals.netValueAfterDiscount;
    }, 0);
  };

  const calculateVat = (items) => {
    return items.reduce((sum, item) => {
      const totals = calculateItemTotals(item);
      return sum + totals.vatAmount;
    }, 0);
  };

  const calculateTotal = (items) => {
    return items.reduce((sum, item) => {
      const totals = calculateItemTotals(item);
      return sum + totals.total;
    }, 0);
  };

  const saveDebitNote = () => {
    // Calculate totals
    const subtotal = calculateSubtotal(debitNote.items);
    const vatAmount = calculateVat(debitNote.items);
    const totalAmount = calculateTotal(debitNote.items);

    setDebitNote({
      ...debitNote,
      subtotal,
      vatAmount,
      totalAmount,
      status: 'Draft'
    });

    // TODO: Implement actual save logic
    console.log('Saving debit note:', debitNote);
  };

  // Get total payments
  const getTotalPayments = () => {
    return debitNote.payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  };
  
  // Get remaining balance
  const getRemainingBalance = () => {
    const total = calculateTotal(debitNote.items);
    const totalPaid = getTotalPayments();
    return total - totalPaid;
  };
  
  // Handle adding a payment
  const handleAddPayment = () => {
    if (!paymentAmount || paymentAmount <= 0) return;
    
    const newPayment = {
      id: Date.now(),
      method: paymentMethod,
      amount: parseFloat(paymentAmount),
      reference: paymentReference,
      date: new Date()
    };
    
    const updatedPayments = [...debitNote.payments, newPayment];
    const totalPaid = updatedPayments.reduce((sum, payment) => sum + payment.amount, 0);
    const totalAmount = calculateTotal(debitNote.items);
    const balanceAmount = totalAmount - totalPaid;
    
    // Determine payment status based on balance
    let paymentStatus = 'pending';
    if (totalPaid >= totalAmount) {
      paymentStatus = 'paid';
    } else if (totalPaid > 0) {
      paymentStatus = 'partial';
    }
    
    setDebitNote({
      ...debitNote,
      payments: updatedPayments,
      paidAmt: totalPaid,
      balanceAmt: balanceAmount,
      paymentStatus
    });
    
    // Reset payment form
    setPaymentAmount(0);
    setPaymentReference('');
  };
  
  // Handle removing a payment
  const handleRemovePayment = (paymentId) => {
    const updatedPayments = debitNote.payments.filter(payment => payment.id !== paymentId);
    const totalPaid = updatedPayments.reduce((sum, payment) => sum + payment.amount, 0);
    const totalAmount = calculateTotal(debitNote.items);
    const balanceAmount = totalAmount - totalPaid;
    
    // Determine payment status based on balance
    let paymentStatus = 'pending';
    if (totalPaid >= totalAmount) {
      paymentStatus = 'paid';
    } else if (totalPaid > 0) {
      paymentStatus = 'partial';
    }
    
    setDebitNote({
      ...debitNote,
      payments: updatedPayments,
      paidAmt: totalPaid,
      balanceAmt: balanceAmount,
      paymentStatus
    });
  };

  const submitDebitNote = async () => {
    try {
      setIsLoading(true);
      
      // Calculate totals
      const subtotal = calculateSubtotal(debitNote.items);
      const vatAmount = calculateVat(debitNote.items);
      const totalAmount = calculateTotal(debitNote.items);
      
      // Validate required fields
      if (!debitNote.debitNoteNumber) {
        alert('Debit note number is required');
        setIsLoading(false);
        return;
      }
      
      if (!debitNote.customer || !debitNote.customer.name) {
        alert('Customer information is required');
        setIsLoading(false);
        return;
      }
      
      if (!debitNote.originalInvoice || !debitNote.originalInvoice.invoiceNo) {
        alert('Reference invoice is required');
        setIsLoading(false);
        return;
      }
      
      if (debitNote.items.length === 0) {
        alert('At least one item is required');
        setIsLoading(false);
        return;
      }
      
      // Format the data according to the API requirements
      const formattedData = {
        masterData: {
          invoiceNo: debitNote.debitNoteNumber,
          invoiceDate: debitNote.date.toISOString(),
          refInvoiceNo: debitNote.originalInvoice.invoiceNo,
          refInvoiceDate: debitNote.originalInvoice.invoiceDate,
          customerCode: debitNote.customer.code || '',
          customerName: debitNote.customer.name,
          customerPhone: debitNote.customer.phone || '',
          customerAddress: debitNote.customer.address || '',
          customerPostalCode: debitNote.customer.postalCode || '',
          customerStreetName: debitNote.customer.streetName || '',
          vat: debitNote.customer.vatNumber || '',
          reason: debitNote.reason || '',
          subtotal: subtotal,
          discountAmount: debitNote.items.reduce((total, item) => {
            const totals = calculateItemTotals(item);
            return total + (totals.discountAmount || 0);
          }, 0),
          taxableAmount: subtotal - debitNote.items.reduce((total, item) => {
            const totals = calculateItemTotals(item);
            return total + (totals.discountAmount || 0);
          }, 0),
          totalTax: vatAmount,
          totalAmount: totalAmount,
          paidAmt: debitNote.paidAmt || 0,
          balanceAmt: debitNote.balanceAmt || totalAmount,
          paymentStatus: debitNote.paymentStatus || 'pending',
          paymentMethod: paymentMethod,
          paymentReference: paymentReference,
          notes: debitNote.notes || ''
        },
        detailData: debitNote.items.map((item, index) => {
          const totals = calculateItemTotals(item);
          return {
            lineNo: index + 1,
            itemCode: item.product.id,
            description: item.description || item.product.name,
            unit: 'Nos',
            qty: item.quantity,
            unitPrice: item.price,
            discountPerc: item.discount,
            discountAmt: totals.discountAmount,
            lineTotal: totals.netValueAfterDiscount,
            vat: item.vatRate,
            vatAmount: totals.vatAmount,
            grandTotal: totals.total,
            uom: (item.unit || item.product?.basicUnit || 'EA').split('(')[0].trim()
          };
        }),
        paymentData: debitNote.payments.map(payment => ({
          paymentDate: payment.date.toISOString(),
          paymentMethod: payment.method,
          paymentReference: payment.reference || '',
          amount: payment.amount,
          notes: ''
        }))
      };
      
      console.log('Submitting debit note data:', formattedData);
      
      // Update debit note status before submitting
      setDebitNote({
        ...debitNote,
        subtotal,
        vatAmount,
        totalAmount,
        status: 'Submitted'
      });
      
      // Send the data to the API
      const response = await api.post('/debitNotes', formattedData);
      
      if (response.data.success) {
        alert('Debit note submitted successfully!');
        // Navigate to the debit notes list
        navigate('/debit-notes');
      } else {
        alert(`Failed to submit debit note: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error submitting debit note:', error);
      alert(`Error submitting debit note: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = (column) => {
    setVisibleColumns({
      ...visibleColumns,
      [column]: !visibleColumns[column]
    });
  };

  // This function is now implemented above with API integration

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          borderRadius: '4px',
          maxWidth: '1800px',
          margin: '0 auto',
          boxShadow: 'none',
          border: 'none'
        }}
      >
        {/* Header Section */}
        <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => navigate('/debit-notes')}
              sx={{ mr: 0.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}
            >
              <CloseIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
                New Debit Note
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.875rem' }}>
                Create a new debit note
              </Typography>
            </Box>
          </Box>
          <Box>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/debit-notes')}
              sx={{ mr: 0.5, fontSize: '0.875rem' }}
            >
              Cancel
            </Button>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
          This debit note will be submitted to ZATCA upon completion. All fields marked with * are required.
        </Alert>

        {/* Main Form Sections */}
        <Grid container spacing={0.5}>
          {/* Debit Note Details Section */}
          <Grid item xs={12} md={6}>
            <DebitNoteDetails
              debitNote={debitNote}
              salesInvoices={[]}
              selectedInvoice={selectedInvoice}
              controlStyle={controlStyle}
              onDebitNoteChange={(newDebitNote) => setDebitNote(newDebitNote)}
              onDateChange={handleDateChange}
              onInvoiceSelect={handleInvoiceSelect}
            />
          </Grid>

          {/* Customer Details Section */}
          <Grid item xs={12} md={6}>
            <CustomerDetails
              debitNote={debitNote}
              controlStyle={controlStyle}
              onCustomerChange={(updatedCustomer) => {
                setDebitNote({
                  ...debitNote,
                  customer: updatedCustomer
                });
              }}
            />
          </Grid>
        </Grid>

        {/* Items Section */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Debit Note Items
        </Typography>

        {/* Using the DebitNoteItemsTable component */}
        <DebitNoteItemsTable 
          items={debitNote.items}
          onItemChange={handleItemChange}
          onRemoveItem={handleRemoveItem}
          visibleColumns={visibleColumns}
          products={products}
          onSettingsClick={handleSettingsClick}
          readOnly={false}
        />

        {/* Invoice Summary Section */}
        <Grid container spacing={0.5} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ height: '100%' }}>
              <DebitNotePaymentSection 
                items={debitNote.items}
                payments={debitNote.payments} 
                grandTotal={calculateTotal(debitNote.items)}
                totalPaid={getTotalPayments()}
                remainingBalance={getRemainingBalance()}
                totalBillAmount={debitNote.totalAmount || 0}
                invoiceId={debitNote.originalInvoice}
              />
            </Box>
          </Grid>
          
          {/* Debit Note Summary */}
          <Grid item xs={12} md={6}>
            <Box sx={{ height: '100%' }}>
              <DebitNoteSummary 
                items={debitNote.items}
                additionalCost={debitNote.additionalCost}
                discountPercentage={debitNote.discountPercentage}
                discountAmount={debitNote.discountAmount}
                referenceInvoice={{
                  amount: debitNote.originalInvoice?.totalAmount || 0
                }}
                onAdditionalCostChange={(value) => setDebitNote({ ...debitNote, additionalCost: value })}
                onDiscountPercentageChange={(percentage, amount) => setDebitNote({ 
                  ...debitNote, 
                  discountPercentage: percentage,
                  discountAmount: amount
                })}
                onDiscountAmountChange={(amount, percentage) => setDebitNote({ 
                  ...debitNote, 
                  discountAmount: amount,
                  discountPercentage: percentage
                })}
                calculateSubtotal={calculateSubtotal}
                calculateVat={calculateVat}
                calculateTotal={calculateTotal}
                getTotalPayments={getTotalPayments}
                getRemainingBalance={getRemainingBalance}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Notes Section */}
        {/* Notes Section */}
        <NotesSection
          value={debitNote.notes}
          onChange={(e) => setDebitNote({ ...debitNote, notes: e.target.value })}
          multilineStyle={multilineStyle}
        />

        {/* Select Invoice Dialog */}
        <SelectInvoiceDialog
          open={selectInvoiceDialogOpen}
          onClose={closeSelectInvoiceDialog}
          onSelectInvoice={handleInvoiceSelect}
          dialogTitle="Select Invoice for Debit Note"
        />

        {/* Column Settings Popover */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleSettingsClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <List>
            {Object.entries(visibleColumns).map(([column, visible]) => (
              <ListItem key={column} button onClick={() => handleColumnToggle(column)}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={visible}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, ' $1')} />
              </ListItem>
            ))}
          </List>
        </Popover>

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            sx={{ fontSize: '0.875rem' }}
          >
            Print
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            onClick={submitDebitNote}
            sx={{ fontSize: '0.875rem' }}
          >
            Submit to ZATCA
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default NewDebitNote; 